import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Input,
  Label,
  Button,
  Container,
  FormFeedback,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import Dropzone from "react-dropzone";
import Select from "react-select";

import Breadcrumbs from "../../../components/Common/Breadcrumb";
import SuccessPopup from "../../../components/SuccessPopup";
import ErrorPopup from "../../../components/ErrorPopup";
import {
  updatePvsSubcategory,
  resetPvsSubcategoryState,
  fetchPvsSubcategoryDetails,
  fetchPvsCategory,
} from "../../../redux/actions/pvs";

function EditSubcategory() {
  document.title = "Edit PVS Subcategory | PVS | Admin Dashboard";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enc_pvs_subcat_id } = useParams();

  const [selectedIconFile, setSelectedIconFile] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Redux selectors
  const updatePvsSubcategoryScreen = useSelector(
    (state) => state.screens.pvs.updatePvsSubcategory
  );
  const fetchPvsReducer = useSelector((state) => state.pvs);
  const categories = useSelector((state) => state.pvs.categoryList || []);

  // Prepare options for react-select
  const categoryOptions = categories.map((cat) => ({
    value: cat.pvs_cat_id,
    label: cat.category,
  }));
  console.log("categoryOptions", categoryOptions);

  // Fetch categories on mount
  useEffect(() => {
    dispatch(fetchPvsCategory());
  }, [dispatch]);

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      subcategory: "",
      pvs_cat_id: "",
      description: "",
      slug: "",
      count: 0,
      icon: null,
    },
    validationSchema: Yup.object({
      pvs_cat_id: Yup.string().required("Please select category"),
      subcategory: Yup.string().required("Please enter subcategory"),
      pvs_cat_id: Yup.string().required("Invalid subcategory ID"),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("category", values.category);
      formData.append("subcategory", values.subcategory);
      formData.append("pvs_cat_id", values.pvs_cat_id);
      formData.append("description", values.description || "");
      if (selectedIconFile) formData.append("pvs_subcategory_icon", selectedIconFile);

      dispatch(updatePvsSubcategory(formData, enc_pvs_subcat_id));
    },
  });

  // Fetch subcategory details on mount
  useEffect(() => {
    if (enc_pvs_subcat_id) {
      dispatch(fetchPvsSubcategoryDetails({ enc_pvs_subcat_id }));
    }
  }, [enc_pvs_subcat_id, dispatch]);

  // Set form values from fetched data
  useEffect(() => {
    if (fetchPvsReducer?.subcategoryDetail) {
      const data = fetchPvsReducer.subcategoryDetail;
      formik.setValues({
        category: data.category || "",
        subcategory: data.subcategory || "",
        pvs_cat_id: data.pvs_cat_id || "", // store enc id for submission
        description: data.description || "",
        slug: data.slug || "",
        count: data.count || 0,
        icon: null,
      });
      if (data.icon_path) setSelectedIconFile(data.icon_path);
    } else if (fetchPvsReducer?.error) {
      setErrorMsg(fetchPvsReducer.message || "Unable to fetch subcategory data");
      setErrorOpen(true);
    }
  }, [fetchPvsReducer]);

  // Handle Redux update success/error
  useEffect(() => {
    if (updatePvsSubcategoryScreen.success) {
      setSuccessOpen(true);
    } else if (updatePvsSubcategoryScreen.error) {
      setErrorMsg(updatePvsSubcategoryScreen.message || "Something went wrong");
      setErrorOpen(true);
    }
  }, [updatePvsSubcategoryScreen]);

  return (
    <React.Fragment>
      <SuccessPopup
        isOpen={successOpen}
        toggle={() => setSuccessOpen(false)}
        onConfirm={() => {
          setSuccessOpen(false);
          dispatch(resetPvsSubcategoryState());
          navigate("/pvs-subcategory", { replace: true });
        }}
        message={updatePvsSubcategoryScreen.message}
      />
      <ErrorPopup
        isOpen={errorOpen}
        toggle={() => {
          setErrorOpen(false);
          dispatch(resetPvsSubcategoryState());
        }}
        message={errorMsg}
      />

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            maintitle="PVS"
            title="Subcategory"
            breadcrumbItem="Edit Subcategory"
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      formik.handleSubmit();
                    }}
                  >
                    <Row>
                      {/* Subcategory */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="subcategory">Subcategory</Label>
                        <Input
                          id="subcategory"
                          name="subcategory"
                          type="text"
                          placeholder="Enter subcategory"
                          value={formik.values.subcategory}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.subcategory && formik.errors.subcategory}
                        />
                        {formik.touched.subcategory && formik.errors.subcategory && (
                          <FormFeedback>{formik.errors.subcategory}</FormFeedback>
                        )}
                      </Col>

                      {/* Category Dropdown using react-select */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="category">Category</Label>
                        <Select
                          id="category"
                          name="category"
                          value={categoryOptions.find(
                            (option) => option.value === formik.values.pvs_cat_id
                          )}
                          onChange={(selectedOption) => {
                            formik.setFieldValue(
                              "category",
                              selectedOption ? selectedOption.label : ""
                            );
                            formik.setFieldValue(
                              "pvs_cat_id",
                              selectedOption ? selectedOption.value : ""
                            );
                          }}
                          options={categoryOptions}
                          onBlur={formik.handleBlur}
                          classNamePrefix="select2-selection"
                          className={
                            formik.touched.category && formik.errors.category
                              ? "is-invalid"
                              : ""
                          }
                        />
                        {formik.touched.category && formik.errors.category && (
                          <FormFeedback className="d-block">
                            {formik.errors.category}
                          </FormFeedback>
                        )}
                      </Col>

                      {/* Subcategory Slug */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="slug">Subcategory Slug</Label>
                        <Input
                          id="slug"
                          name="slug"
                          placeholder="Subcategory slug"
                          value={formik.values.slug}
                          disabled
                        />
                      </Col>

                      {/* Subcategory Count */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="count">Subcategory Count</Label>
                        <Input
                          id="count"
                          name="count"
                          value={formik.values.count}
                          disabled
                        />
                      </Col>

                      {/* Description */}
                      <Col md={6} className="mb-3">
                        <Label htmlFor="description">Description</Label>
                        <textarea
                          id="description"
                          name="description"
                          className={`form-control ${
                            formik.touched.description && formik.errors.description
                              ? "is-invalid"
                              : ""
                          }`}
                          rows={2}
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.description && formik.errors.description && (
                          <FormFeedback>{formik.errors.description}</FormFeedback>
                        )}
                      </Col>

                      {/* Icon Upload */}
                      <Col md={4} className="mb-3">
                        <Label htmlFor="icon">Subcategory Icon</Label>
                        <Dropzone
                          onDrop={(acceptedFiles) => {
                            setSelectedIconFile(acceptedFiles[0]);
                            formik.setFieldValue("icon", acceptedFiles[0]);
                          }}
                          multiple={false}
                          accept={{ "image/*": [] }}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <div className="dropzone">
                              <div className="dz-message needsclick" {...getRootProps()}>
                                <input {...getInputProps()} />
                                <div className="mb-3">
                                  <i className="mdi mdi-cloud-upload display-4 text-muted"></i>
                                </div>
                                <h5>Drop files here or click to upload</h5>
                                {selectedIconFile && (
                                  <div className="mt-2">
                                    <img
                                      src={
                                        typeof selectedIconFile === "string"
                                          ? selectedIconFile
                                          : URL.createObjectURL(selectedIconFile)
                                      }
                                      alt="Icon Preview"
                                      style={{
                                        maxWidth: "150px",
                                        maxHeight: "100px",
                                        borderRadius: "4px",
                                        border: "1px solid #eee",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Dropzone>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-center mt-3">
                      <Button type="submit" color="primary" size="lg" className="w-25">
                        Submit
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default EditSubcategory;
